import {
  Box,
  Button,
  Checkbox,
  Flex,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { SearchSelect } from '~/packages/common-frontend/components/SearchSelect';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import {
  useGetCreditFacilityCapitalProviderRepresentativeQuery,
  useGetCreditFacilityExternalFacilityIdQuery,
} from '~/packages/credit-facility/requests/requests.gql.generated';
import { useGetCreditFacilityFullLoansQuery } from '~/packages/loans/requests/loans.gql.generated';

const ContinueButton = ({
  selectedCreditFacilityId,
  canClickContinue,
  setCurrentStepIndex,
}: {
  selectedCreditFacilityId: string;
  canClickContinue: boolean;
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const {
    selectedEntities: {
      creditFacility: { updateCreditFacilityLoans },
    },
  } = useCreateInvoiceContext();

  const {
    data: creditFacilityFullLoansData,
    loading: isCreditFacilityFullLoansLoading,
  } = useGetCreditFacilityFullLoansQuery({
    variables: {
      creditFacilityId: selectedCreditFacilityId,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (creditFacilityFullLoansData?.creditFacility?.loans.length) {
      updateCreditFacilityLoans(
        creditFacilityFullLoansData.creditFacility.loans,
      );
    }
  }, [creditFacilityFullLoansData, updateCreditFacilityLoans]);

  return (
    <Tooltip
      label={canClickContinue ? '' : 'All mandatory fields must be completed'}
    >
      <Button
        variant="primary"
        colorScheme="v2-aurora"
        isDisabled={!canClickContinue}
        isLoading={isCreditFacilityFullLoansLoading}
        onClick={() => setCurrentStepIndex(1)}
      >
        Continue
      </Button>
    </Tooltip>
  );
};

export const CreateMultiInvoiceDetails = ({
  setCurrentStepIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const navigate = useNavigate();
  const { facilities } = useCreditFacilitiesOrThrow();

  const {
    invoice,
    updateInvoice,
    resetInvoice,
    resetInvoiceComponents,
    selectedEntities: {
      loan: { loan: selectedLoan },
      creditFacility: { creditFacility, updateCreditFacility },
    },
  } = useCreateInvoiceContext();

  const capitalProviderRepresentativeOrganizationQuery = useGetCreditFacilityCapitalProviderRepresentativeQuery(
    {
      skip: !creditFacility,
      variables: {
        creditFacilityId: creditFacility?.id ?? '',
      },
    },
  );

  if (capitalProviderRepresentativeOrganizationQuery.error) {
    throw capitalProviderRepresentativeOrganizationQuery.error;
  }

  // Update payment destination when the capital provider representative query completes
  useEffect(() => {
    if (
      capitalProviderRepresentativeOrganizationQuery.data?.creditFacility
        .capitalProviderRepresentativeOrganization?.id &&
      invoice.creditFacilityId === creditFacility?.id // Only update if this is still the current facility
    ) {
      const capitalProviderRepresentativeId =
        capitalProviderRepresentativeOrganizationQuery.data.creditFacility
          .capitalProviderRepresentativeOrganization.id;

      // Only update if the value is different to avoid unnecessary updates
      if (
        invoice.paymentDestinationOrganizationId !==
        capitalProviderRepresentativeId
      ) {
        updateInvoice({
          field: 'paymentDestinationOrganizationId',
          value: capitalProviderRepresentativeId,
        });
      }
    }
  }, [
    capitalProviderRepresentativeOrganizationQuery.data,
    invoice.creditFacilityId,
    invoice.paymentDestinationOrganizationId,
    creditFacility?.id,
    updateInvoice,
  ]);

  const externalFacilityIdQuery = useGetCreditFacilityExternalFacilityIdQuery({
    skip: !creditFacility,
    variables: {
      creditFacilityId: creditFacility?.id ?? '',
    },
  });
  if (externalFacilityIdQuery.error) throw externalFacilityIdQuery.error;

  const creditFacilityOptions = useMemo(() => {
    return facilities.map((facility) => ({
      value: facility.id,
      label: facility.name,
    }));
  }, [facilities]);

  const onCancel = useCallback(() => {
    resetInvoice();
    navigate('/invoices');
  }, [resetInvoice, navigate]);

  const canClickContinue = useMemo(() => {
    if (!invoice.creditFacilityId || !invoice.dueDate) {
      return false;
    }

    return true;
  }, [invoice]);

  const minAndMaxDueDate = useMemo(() => {
    const today = new Date();
    const fiveYearsBefore = new Date().setFullYear(today.getFullYear() - 5);
    const fiveYearsLater = new Date().setFullYear(today.getFullYear() + 5);

    return {
      minDate: selectedLoan
        ? commonDate.fullDateStrToDate(selectedLoan.dates.originationDate)
        : new Date(fiveYearsBefore),
      maxDate: new Date(fiveYearsLater),
    };
  }, [selectedLoan]);

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={0} />
      </Box>

      {/* Guided Form Step 1 */}
      <Flex direction="column" flexGrow={1}>
        <VStack mb="24px" w="100%" alignItems="flex-start">
          <Typography fontWeight="bold">Facility</Typography>
          <SearchSelect
            options={creditFacilityOptions}
            value={creditFacilityOptions.find(
              (option) => option.value === invoice.creditFacilityId,
            )}
            onChange={(nextCreditFacilityId) => {
              updateInvoice({
                field: 'creditFacilityId',
                value: nextCreditFacilityId,
              });

              const newCreditFacility = facilities.find(
                (facility) => facility.id === nextCreditFacilityId,
              );

              finleyAssert(
                newCreditFacility,
                `Credit facility must exist for [creditFacilityId: ${nextCreditFacilityId}]`,
              );

              updateCreditFacility(newCreditFacility);

              // update payment source & destination org ids based on selected cf
              // currently we only allow payment source to be borrower
              // and payment destination to be admin agent cap provider (handled in useEffect)
              updateInvoice({
                field: 'paymentSourceOrganizationId',
                value: newCreditFacility.borrowerId,
              });
              // Payment destination will be updated automatically when the query completes (see useEffect above)

              // reset invoice type to default
              updateInvoice({
                field: 'invoiceType',
                value: 'loan',
              }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level

              // reset finleyLoanId
              updateInvoice({
                field: 'finleyLoanId',
                value: undefined,
              }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level

              // reset invoice items
              resetInvoiceComponents();
            }}
            isSelectedColor={colors.gray[50]}
            isFocusedColor={colors.gray[50]}
          />
          {invoice.creditFacilityId && creditFacility && (
            <Typography variant="base-muted">
              External Facility ID:{' '}
              {externalFacilityIdQuery.data?.creditFacility
                .externalFacilityId ?? '-'}
            </Typography>
          )}
        </VStack>

        <VStack mb="24px" w="100%" alignItems="flex-start">
          <Typography fontWeight="bold">Due Date</Typography>
          <SimpleDatePicker
            tz={clientTimeZone}
            selectedDate={
              invoice.dueDate
                ? commonDate.fullDateStrToDate(invoice.dueDate)
                : undefined
            }
            setSelectedDate={(date: Date | null) => {
              // date must exist. this datepicker is not clearable
              if (date) {
                const newDueDate = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                updateInvoice({ field: 'dueDate', value: newDueDate });

                // product decision: when updating due date, we want to reset the invoice
                resetInvoiceComponents();

                const isInvoiceDueDateInFuture =
                  newDueDate > commonDate.currentDateStr();

                // update invoice autoSettle to false if due date is in the past or current date
                // can only auto settle if due date is in the future
                if (!isInvoiceDueDateInFuture) {
                  updateInvoice({
                    field: 'autoSettle',
                    value: false,
                  });
                }
              }
            }}
            width="100%"
            dateFormat="MMMM d, yyyy"
            minDate={minAndMaxDueDate.minDate}
            maxDate={minAndMaxDueDate.maxDate}
          />
          <Tooltip
            label="Select to automatically mark invoice as paid if due date is in the future"
            placement="bottom-start"
          >
            <Box>
              <Checkbox
                mt="16px"
                onChange={(e) =>
                  updateInvoice({
                    field: 'autoSettle',
                    value: Boolean(e.target.checked),
                  })
                }
                isChecked={invoice.autoSettle}
                isDisabled={
                  !invoice.dueDate ||
                  invoice.dueDate <= commonDate.currentDateStr()
                } // can only auto settle if due date is in the future
              >
                <Typography variant="base-muted">
                  Automatically mark as &apos;Paid&apos; on Due Date.
                </Typography>
              </Checkbox>
            </Box>
          </Tooltip>
        </VStack>

        <Box mb="24px">
          <Flex alignItems="center" justifyContent="space-between" mb="16px">
            <Typography fontWeight="bold">Memo</Typography>
            <Typography variant="base-muted">Optional</Typography>
          </Flex>
          <Textarea
            value={invoice.memo ?? ''}
            onChange={(e) => {
              updateInvoice({ field: 'memo', value: e.target.value });
            }}
            placeholder="Leave a memo"
          />
        </Box>
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" py="40px">
        <Button variant="secondary" colorScheme="v2-aurora" onClick={onCancel}>
          Cancel
        </Button>
        {invoice.creditFacilityId && (
          <ContinueButton
            selectedCreditFacilityId={invoice.creditFacilityId}
            canClickContinue={canClickContinue}
            setCurrentStepIndex={setCurrentStepIndex}
          />
        )}
      </Flex>
    </Flex>
  );
};
